import axios from "@/plugins/axios";
import logger, {
  createAttemptId,
  getAppsLogsDiagnosticContext,
  getErrorDiagnostic,
} from "@/plugins/appsLogsCore";

function pushDiagnostic(extra = {}) {
  return getAppsLogsDiagnosticContext({
    extra: {
      notificationPermission:
        typeof Notification !== "undefined"
          ? Notification.permission
          : "unsupported",
      serviceWorkerSupported:
        typeof navigator !== "undefined" && "serviceWorker" in navigator,
      pushManagerSupported:
        typeof window !== "undefined" && "PushManager" in window,
      serviceWorkerControllerExists:
        typeof navigator !== "undefined" &&
        navigator.serviceWorker &&
        !!navigator.serviceWorker.controller,
      ...extra,
    },
  });
}

export default {
  data() {
    return {
      publicVapidKey:
        "BM0y8CuzwW3kHMzRmTMDz_t2b5y7VWEV9MDk7i0eeEWioo2pJd9SclJsB6Bgi8sB2YFpGvTz-Lqf785cYIfFV6o",
      checkedSubscription: false,
      isSubscribed: null,
      register: null,
      subscription: null,
    };
  },
  mounted() {
    if (this.$isKioskSession) return;

    if (!this.isPushSupported()) {
      logger.info("push.not_supported_in_current_context", pushDiagnostic());
      return;
    }

    this.registerSw();
  },

  methods: {
    isPushSupported() {
      return (
        typeof window !== "undefined" &&
        typeof navigator !== "undefined" &&
        "Notification" in window &&
        "serviceWorker" in navigator &&
        "PushManager" in window
      );
    },
    async registerSw() {
      if (this.$isKioskSession) return;

      if (!this.isPushSupported()) {
        logger.info("push.registration_skipped_unsupported", pushDiagnostic());
        return;
      }

      const pushAttemptId = createAttemptId("push_register");
      logger.info(
        "push.service_worker_registration_started",
        pushDiagnostic({ pushAttemptId, scope: "/push-notifications" })
      );
      try {
        this.register = await navigator.serviceWorker.register("/push-sw.js", {
          scope: "/push-notifications",
        });
        logger.info(
          "push.service_worker_registration_succeeded",
          pushDiagnostic({ pushAttemptId, scope: this.register.scope })
        );
        await this.getSubscription(pushAttemptId);
      } catch (error) {
        logger.error(
          error,
          pushDiagnostic({
            pushAttemptId,
            step: "service_worker_register",
            ...getErrorDiagnostic(error),
          })
        );
      }
    },
    async getSubscription(
      pushAttemptId = createAttemptId("push_subscription")
    ) {
      logger.info(
        "push.subscription_check_started",
        pushDiagnostic({ pushAttemptId })
      );
      await this.register.pushManager
        .getSubscription()
        .then(async (subscription) => {
          this.subscription = subscription;
          logger.info(
            "push.browser_subscription_loaded",
            pushDiagnostic({
              pushAttemptId,
              hasBrowserSubscription: !!subscription,
              endpointHost:
                subscription && subscription.endpoint
                  ? new URL(subscription.endpoint).host
                  : undefined,
            })
          );
          await this.getServerSubscription()
            .then((res) => {
              // console.log("getServerSubscription: ", res);
              const endpoint = res.data.data.endpoint;
              if (subscription && endpoint == subscription.endpoint) {
                this.isSubscribed = true;
              }
              logger.info(
                "push.server_subscription_loaded",
                pushDiagnostic({
                  pushAttemptId,
                  status: res.status,
                  hasServerSubscription: !!endpoint,
                  subscriptionMatches: !!this.isSubscribed,
                })
              );
            })
            .catch((error) => {
              logger.warn(
                "push.server_subscription_load_failed",
                pushDiagnostic({
                  pushAttemptId,
                  ...getErrorDiagnostic(error),
                })
              );
            });
        })
        .catch((error) => {
          logger.warn(
            "push.browser_subscription_load_failed",
            pushDiagnostic({ pushAttemptId, ...getErrorDiagnostic(error) })
          );
        })
        .finally(() => {
          this.checkedSubscription = true;
          logger.info(
            "push.subscription_check_completed",
            pushDiagnostic({
              pushAttemptId,
              checkedSubscription: this.checkedSubscription,
              isSubscribed: this.isSubscribed,
            })
          );
        });
      // console.log("this.subscription", this.subscription);
      return this.isSubscribed;
    },
    getServerSubscription() {
      return axios.get(`/user/notifications/push`);
      // .then((res) => {
      //   console.log("res is: ", res);
      // })
      // .catch(() => {
      //   // console.log("ERROR: ", err);
      // });
    },
    async allowPush() {
      logger.info("push.permission_prompt_accepted", pushDiagnostic());
      if (localStorage.getItem("deniedPush")) {
        localStorage.removeItem("deniedPush");
      }
      await this.subscribe();
      // console.log("Got to push subscribe");
    },
    denyPush() {
      localStorage.setItem("deniedPush", "true");
      logger.info("push.permission_prompt_denied_in_app", pushDiagnostic());
    },
    async subscribe() {
      if (!this.isPushSupported() || !this.register) {
        logger.info(
          "push.subscribe_skipped_unsupported_or_unregistered",
          pushDiagnostic({ hasRegister: !!this.register })
        );
        return;
      }

      const pushAttemptId = createAttemptId("push_subscribe");
      logger.info("push.subscribe_started", pushDiagnostic({ pushAttemptId }));
      try {
        const subscription = await this.register.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.publicVapidKey,
        });
        logger.info(
          "push.browser_subscribe_succeeded",
          pushDiagnostic({
            pushAttemptId,
            endpointHost: subscription.endpoint
              ? new URL(subscription.endpoint).host
              : undefined,
          })
        );
        await this.sendSubscription(subscription, pushAttemptId);
      } catch (error) {
        logger.error(
          error,
          pushDiagnostic({
            pushAttemptId,
            step: "browser_subscribe",
            ...getErrorDiagnostic(error),
          })
        );
      }
    },

    async sendSubscription(subscription, pushAttemptId) {
      await axios
        .post(`/user/notifications/push/subscribe`, subscription)
        .then((res) => {
          logger.info(
            "push.server_subscribe_succeeded",
            pushDiagnostic({ pushAttemptId, status: res.status })
          );
        })
        .catch((error) => {
          logger.warn(
            "push.server_subscribe_failed",
            pushDiagnostic({ pushAttemptId, ...getErrorDiagnostic(error) })
          );
        });
    },
    async unsubscribe() {
      try {
        this.subscription
          .unsubscribe()
          .then(() => {
            // console.log("unsubscribe: ", unsubed);
          })
          .catch(() => {
            // console.log("For some reason you could not unsubscribe", e);
          })
          .finally(async () => {
            this.unsubscribeInServer();
          });
      } catch (err) {
        console.error(err);
        this.unsubscribeInServer();
      }
    },
    async unsubscribeInServer() {
      await axios
        .post(
          `/user/notifications/push/unsubscribe`,
          this.subscription.toJSON()
        )
        .then(() => {
          // console.log("res is: ", res);
        })
        .catch(() => {
          // console.log("ERROR: ", err);
        });
    },
  },
};
